import React, { useState, useCallback, useContext } from "react";
import { DemandWalkEditsContext } from "./context";
import {
  PlannerOverrideInputs,
  FinalForecastInputs,
  YoYPercentageInputs,
} from "./type";
import { useApi, useWasm } from "../app/provider";
import {
  useUploadAndTriggerMyRunMutation,
  UploadAndTriggerMyRunMutationVariables,
  MyRunSettingsInput,
} from "../../services/api/__generated__/backend_gateway-types";
import { VersionedDataContext } from "../versioned_data/context";

import { convertFinalForecastInputsToParquet } from "../../utils/mui_table_utils";
import { canonicalSegmentOrder } from "../../utils/demand_plan_grain";
import { SysMessagesContext } from "../sys_messages/context";
import { SysMessageLevel } from "../sys_messages/type";
import { useLocation } from "react-router-dom";

// Import the parquet generation libraries
import {
  Table as WasmTable,
  WriterPropertiesBuilder,
  Compression,
  writeParquet,
} from "parquet-wasm";

import {
  makeBuilder,
  Utf8,
  List,
  Float64,
  Timestamp,
  TimeUnit,
  tableToIPC,
  Field,
  Table,
} from "apache-arrow";

interface DemandWalkEditsProviderProps {
  children: React.ReactNode;
}

export const DemandWalkEditsProvider: React.FC<
  DemandWalkEditsProviderProps
> = ({ children }) => {
  const { apiService } = useApi();
  const { wasmInitialized, wasmError } = useWasm();
  const { mainRun, lastSuccessfulRun } = useContext(VersionedDataContext);
  const { addSystemMessage } = useContext(SysMessagesContext);
  const { search } = useLocation();

  const [plannerOverrideInputs, setPlannerOverrideInputs] =
    useState<PlannerOverrideInputs>({});
  const [finalForecastInputs, setFinalForecastInputs] =
    useState<FinalForecastInputs>({});
  const [yoyPercentageInputs, setYoYPercentageInputs] =
    useState<YoYPercentageInputs>({});

  const [editMode, setEditModeState] = useState<boolean>(false);

  const [uploadAndTriggerRun, { loading }] = useUploadAndTriggerMyRunMutation({
    client: apiService.getClient(),
  });

  // Convenience methods for edit state
  const setEditMode = useCallback((mode: boolean) => {
    setEditModeState(mode);
  }, []);

  // Clear all edits
  const clearEdits = useCallback(() => {
    setPlannerOverrideInputs({});
    setFinalForecastInputs({});
    setYoYPercentageInputs({});
  }, []);

  // Discard all changes (clears demand walk edits + resets edit state)
  const discardChanges = useCallback(() => {
    clearEdits();
    setEditModeState(false);
  }, [clearEdits]);

  // Save edits and trigger run function (moved from EditModeToggle)
  const saveEditsAndTriggerRun = useCallback(
    async (runName: string) => {
      try {
        // Use mainRun if available, otherwise fall back to lastSuccessfulRun
        const runToUse = mainRun || lastSuccessfulRun;

        if (!runToUse) {
          console.error("No run available for upload and trigger");
          return;
        }

        // Decode segments and measure from URL parameters
        const urlParams = new URLSearchParams(search);

        // Extract and decode segments
        const segmentsParam = urlParams.get("segments");
        if (!segmentsParam) {
          addSystemMessage({
            level: SysMessageLevel.DANGER,
            message:
              "No segments parameter found in URL. Cannot save edits without segmentation information.",
          });
          return;
        }

        let segments: string[];
        try {
          segments = JSON.parse(segmentsParam);
        } catch (error) {
          addSystemMessage({
            level: SysMessageLevel.DANGER,
            message: "Invalid segments format in URL. Cannot save edits.",
          });
          return;
        }

        if (!segments || segments.length === 0) {
          addSystemMessage({
            level: SysMessageLevel.DANGER,
            message:
              "Empty segments array in URL. Cannot save edits without segmentation information.",
          });
          return;
        }

        // Extract measure
        const measure = urlParams.get("measure") || "quantity";

        // Grid row keys are in canonical (sorted) field order; pair the saved
        // values with the same order (see demand_plan_grain, SUNGLAS-56).
        const parquetRows = convertFinalForecastInputsToParquet(
          finalForecastInputs,
          canonicalSegmentOrder(segments),
          measure,
          addSystemMessage
        );

        if (parquetRows.length === 0) {
          addSystemMessage({
            level: SysMessageLevel.WARNING,
            message:
              "No valid edits found to save. Please make some changes first.",
          });
          return;
        }

        // Generate parquet file with proper List(String) types using apache-arrow + parquet-wasm

        // Check if WASM is initialized
        if (!wasmInitialized) {
          throw new Error(
            "WASM not initialized yet. Please wait for initialization to complete."
          );
        }

        if (wasmError) {
          throw new Error(`WASM initialization failed: ${wasmError}`);
        }

        // Build Arrow columns with explicit dtypes
        const listUtf8Field = new Field("item", new Utf8());
        const listUtf8 = new List(listUtf8Field);
        const tsUTCus = new Timestamp(TimeUnit.MICROSECOND, "UTC");

        // Use makeBuilder with the correct types
        const groupLevelB = makeBuilder({ type: listUtf8 });
        const groupValueB = makeBuilder({ type: listUtf8 });
        const startB = makeBuilder({ type: tsUTCus });
        const endB = makeBuilder({ type: tsUTCus });
        const qtyB = makeBuilder({ type: new Float64() });
        const typeB = makeBuilder({ type: new Utf8() });
        const parentRunIdB = makeBuilder({ type: new Utf8() });

        // Append all rows
        for (const row of parquetRows) {
          groupLevelB.append(row.group_level);
          groupValueB.append(row.group_value);
          startB.append(row.start);
          endB.append(row.end);
          qtyB.append(row.quantity_override);
          typeB.append(row.override_type);
          parentRunIdB.append(
            JSON.stringify(runToUse.runSettings, (key, value) => {
              if (key === "__typename") {
                return undefined; // Remove __typename
              }
              return value;
            })
          );
        }

        // Finish builders -> Arrow Vectors
        const vectors = {
          group_level: groupLevelB.finish().toVector(),
          group_value: groupValueB.finish().toVector(),
          start: startB.finish().toVector(),
          end: endB.finish().toVector(),
          quantity_override: qtyB.finish().toVector(),
          override_type: typeB.finish().toVector(),
          parent_run_id: parentRunIdB.finish().toVector(),
        };

        // Create Arrow table from vectors using the correct method
        const table = new Table(vectors);

        // Convert Arrow->WASM Table, write Parquet
        const wasmTable = WasmTable.fromIPCStream(tableToIPC(table, "stream"));

        const props = new WriterPropertiesBuilder()
          .setCompression(Compression.ZSTD)
          .build();

        const parquetBytes: Uint8Array = writeParquet(wasmTable, props);

        // Convert to base64 for the mutation
        const fileContent = btoa(
          String.fromCharCode(...new Uint8Array(parquetBytes))
        );

        const runSettings: MyRunSettingsInput = {
          inputId: runToUse.runSettings.inputId,
          workflowId: runToUse.runSettings.workflowId,
          settingsNameAndVersion: runToUse.runSettings.settingsNameAndVersion,
          runAttemptId: runToUse.runSettings.runAttemptId,
        };

        const mutationVariables: UploadAndTriggerMyRunMutationVariables = {
          fileContent,
          filename: "demand_walk_layer.parquet",
          runSettings,
          runName: runName || null,
        };

        await uploadAndTriggerRun({
          variables: mutationVariables,
        });
        setEditMode(false);
      } catch (error: unknown) {
        addSystemMessage({
          level: SysMessageLevel.DANGER,
          message: "Failed to upload and trigger run.",
        });
        console.error("Failed to upload and trigger run:", error);
      }
    },
    [
      uploadAndTriggerRun,
      mainRun,
      lastSuccessfulRun,
      finalForecastInputs,
      addSystemMessage,
      search,
      wasmInitialized,
      wasmError,
      setEditMode,
    ]
  );

  const value = {
    // Demand walk specific state
    plannerOverrideInputs,
    finalForecastInputs,
    yoyPercentageInputs,

    // Edit state (moved from LayoutContext)
    editMode,

    // Loading state from mutations
    loading,

    // State setters
    setPlannerOverrideInputs,
    setFinalForecastInputs,
    setYoYPercentageInputs,

    // Convenience methods for edit state
    setEditMode,
    saveEditsAndTriggerRun,

    // Actions
    discardChanges,
  };

  return (
    <DemandWalkEditsContext.Provider value={value}>
      {children}
    </DemandWalkEditsContext.Provider>
  );
};
